import { addColumnAfter, addColumnBefore, addRowAfter, addRowBefore, deleteColumn, deleteRow, deleteTable, isInTable, mergeCells, selectionCell, setCellAttr, splitCell, toggleHeaderColumn, toggleHeaderRow, } from "prosemirror-tables";
import React, { useMemo, useRef, useState } from "react";
import { globalModalsZIndex } from "@smartsuite/react-ui/lib/constants/zIndex";
import cx from "classnames";
import { DotsMenu } from "@smartsuite/react-ui/lib/components/dropdowns/DotsMenu/DotsMenu";
import { SolutionColorVariables } from "@smartsuite/types";
import { MenuOption } from "@smartsuite/react-ui/lib/components/dropdowns/MenuOption/MenuOption";
import { MenuLineSeparator } from "@smartsuite/react-ui/lib/components/dropdowns/MenuLineSeparator/MenuLineSeparator";
import { SubmenuMenuOption } from "@smartsuite/react-ui/lib/components/dropdowns/SubmenuMenuOption/SubmenuMenuOption";
import { MenuOptionWrapper } from "@smartsuite/react-ui/lib/components/dropdowns/MenuOptionWrapper/MenuOptionWrapper";
import { EditorPopup } from "../../popup";
import { DeleteTableDialog } from "./DeleteTableDialog";
import { text } from "./MenuView.text";
export const MenuView = ({ view, }) => {
    const menuRef = useRef();
    const [popupVisible, setPopupVisible] = useState(false);
    const [confirmationDialogActive, setConfirmationDialogActive] = useState(false);
    const hidden = useMemo(() => !isInTable(view.state), [view.state]);
    const popupActionRef = useRef(null);
    if (hidden) {
        return null;
    }
    const handleDeleteTable = () => {
        setPopupVisible(false);
        setConfirmationDialogActive(true);
    };
    const handleConfirmDelete = () => {
        setConfirmationDialogActive(false);
        deleteTable(view.state, view.dispatch.bind(view));
        view.focus();
    };
    const handleCancelDelete = () => {
        setConfirmationDialogActive(false);
        view.focus();
    };
    const cell = selectionCell(view.state);
    const cellElement = view.nodeDOM(cell.pos);
    const mergeCellsActive = mergeCells(view.state);
    const splitCellActive = splitCell(view.state);
    const handleRunAction = (action) => () => {
        var _a;
        action(view.state, view.dispatch.bind(view));
        setPopupVisible(false);
        (_a = popupActionRef === null || popupActionRef === void 0 ? void 0 : popupActionRef.current) === null || _a === void 0 ? void 0 : _a.update();
        view.focus();
    };
    return (React.createElement(EditorPopup, { view: view, referenceElement: cellElement, placement: "top-end", offsetTop: -26, offsetLeft: -2, actionsRef: popupActionRef },
        React.createElement(DotsMenu, { ref: menuRef, opened: popupVisible, minWidth: 180, maxHeight: 420, onToggle: setPopupVisible, appendToBody: true, backgroundColors: {
                default: "transparent",
                hover: SolutionColorVariables.sc2,
                active: SolutionColorVariables.sc2,
            }, colors: {
                hover: SolutionColorVariables.sc1,
                active: SolutionColorVariables.sc1,
            } },
            React.createElement(MenuOption, { onClick: handleRunAction(addRowBefore) }, text.tableActions.insertRowAbove),
            React.createElement(MenuOption, { onClick: handleRunAction(addRowAfter) }, text.tableActions.insertRowBelow),
            React.createElement(MenuOption, { onClick: handleRunAction(addColumnBefore) }, text.tableActions.insertColumnLeft),
            React.createElement(MenuOption, { onClick: handleRunAction(addColumnAfter) }, text.tableActions.insertColumnRight),
            (mergeCellsActive || splitCellActive) && React.createElement(MenuLineSeparator, null),
            mergeCellsActive && (React.createElement(MenuOption, { onClick: handleRunAction(mergeCells) }, text.tableActions.mergeCells)),
            splitCellActive && (React.createElement(MenuOption, { onClick: handleRunAction(splitCell) }, text.tableActions.splitCell)),
            React.createElement(MenuLineSeparator, null),
            React.createElement(MenuOption, { onClick: handleRunAction(toggleHeaderColumn) }, text.tableActions.toggleHeaderColumn),
            React.createElement(MenuOption, { onClick: handleRunAction(toggleHeaderRow) }, text.tableActions.toggleHeaderRow),
            React.createElement(SubmenuMenuOption, { parentMenu: menuRef, name: text.tableActions.setBackgroundColor, zIndex: globalModalsZIndex, autohide: true },
                React.createElement(MenuOptionWrapper, null,
                    React.createElement(MenuOption, { onClick: handleRunAction(setCellAttr("background", null)) },
                        React.createElement("span", { className: cx("table-cell-dropdown__color-tag", `table-cell-dropdown__color-tag--default`), style: { background: "transparent" } }, "A"),
                        text.tableActions.none),
                    colors.map(({ label, value, className }) => (React.createElement(MenuOption, { key: label, onClick: handleRunAction(setCellAttr("background", value)) },
                        React.createElement("span", { className: cx("table-cell-dropdown__color-tag", `table-cell-dropdown__color-tag--${className}`), style: { background: value } }, "A"),
                        label))))),
            React.createElement(MenuLineSeparator, null),
            React.createElement(MenuOption, { className: "table-cell-dropdown__option--danger", onClick: handleRunAction(deleteColumn) }, text.tableActions.deleteColumn),
            React.createElement(MenuOption, { className: "table-cell-dropdown__option--danger", onClick: handleRunAction(deleteRow) }, text.tableActions.deleteRow),
            React.createElement(MenuOption, { className: "table-cell-dropdown__option--danger", onClick: handleDeleteTable }, text.tableActions.deleteTable)),
        React.createElement(DeleteTableDialog, { active: confirmationDialogActive, onConfirm: handleConfirmDelete, onCancel: handleCancelDelete })));
};
const colors = [
    {
        label: text.colors.gray,
        value: "rgb(235,236,237)",
        className: "gray",
    },
    {
        label: text.colors.white,
        value: "rgb(255,255,255)",
        className: "white",
    },
    {
        label: text.colors.brown,
        value: "rgb(233,229,227)",
        className: "brown",
    },
    {
        label: text.colors.orange,
        value: "rgb(250,235,221)",
        className: "orange",
    },
    {
        label: text.colors.yellow,
        value: "rgb(251,243,219)",
        className: "yellow",
    },
    {
        label: text.colors.green,
        value: "rgb(221,237,234)",
        className: "green",
    },
    {
        label: text.colors.blue,
        value: "rgb(221,235,241)",
        className: "blue",
    },
    {
        label: text.colors.purple,
        value: "rgb(234,228,242)",
        className: "purple",
    },
    {
        label: text.colors.pink,
        value: "rgb(244,223,235)",
        className: "pink",
    },
    {
        label: text.colors.red,
        value: "rgb(251,228,228)",
        className: "red",
    },
];
